// assistantAskRoute.js — THE ASK ROUTER (the workspace AI overhaul, 2026-10-28).
//
// Nika's debugged thread (dev, 2026-10-27): one conversation said "Dashboard ·
// 7 questions" in its head, "Describe a dashboard…" in the composer and Pages
// in the Ask menu, while every answer came as chat. A document ask became a
// chat answer in one view and a generated page in another, and a board that
// the service could not make became a silent chat turn. ONE decision now
// routes every ask, and ONE reader names the view the labels show:
//
//   THE VIEW IS THE DEFAULT. Chat answers in the chat; Pages makes a Fly page;
//   the Home tab answers a question in the chat; Apps keeps its builder
//   conversation; Present makes a live deck.
//   A CLEAR INTENT SWITCHES, from any view: a dashboard → a Fly page (a
//   generated board when Fly is not here, else the chat with one note); an app
//   → the app builder; slides → a live deck; a website → the site builder when
//   one is registered; a document, an open or an action → the chat thread (its
//   tools do those); the tour → the tour. A switch says so in one quiet line
//   with one way back (assistantAskNotify, assistantAskBack). Never modal.
//   AN EXPLICIT ASK MODE (the composer's focus panel) and a question about a
//   picked region keep their own routes (functions/homeAiSend.js).
//
// The person's stored mode is never written here: a switch on the home moves
// the home's VIEW with the tabs' local form (functions/homeView.js
// homeViewSelect — `setAssistantMode(m, { local: true })`).
//
// Addendum (orchestrator, 2026-10-28): a stored 'dashboard' mode reads as the
// Pages view and 'tools' as the Chat view — mapped here at read time, the
// stored value untouched.
//
// FRANK: every export is self-contained — no import, no module-scope helper,
// no bare call to a sibling (frank revives each export alone, FA204); a sibling
// is reached through `this.call('name', …)`.

// WHAT THE ASK WANTS: 'question' | 'dashboard' | 'document' | 'website' |
// 'app' | 'deck' | 'open' | 'action' | 'tour'. Conservative by design: only a
// request verb at the start (after "please", "can you", "გთხოვთ", …) with a
// known object, or a bare noun phrase ("a board of my tickets"), names a
// surface. A question, a status ask ("are you still doing the document?"), a
// negation ("do not build a tracker") and anything else is a 'question' — the
// view answers it. A QUOTED TITLE is a name, never a verb or an object ("Open
// the 'Lead Generation Strategy' document" opens it). English and Georgian,
// the informal and the polite plural (შექმენი / შექმენით).
export const assistantAskIntent = function assistantAskIntent(text) {
  const raw = String(text == null ? '' : text)
    .replace(/[’‘]/g, "'")
    .replace(/\s+/g, ' ')
    .trim()
  if (!raw) return 'question'
  // A quoted title reads as one neutral word ('qq').
  const t = (' ' + raw + ' ')
    .replace(/([\s(:,])["“„«'][^"“”„«»'\n]{1,160}["“”»'](?=[\s.,!?;:)])/g, '$1 qq ')
    .toLowerCase()
    .replace(/\s+/g, ' ')
    .trim()
  const end = '(?=$|[\\s.,!?;:])'

  // THE TOUR — the Symbols tour, from any view. The tour word ends the ask (a
  // travel workspace's "show me the tour bookings" stays a question).
  const tour =
    /\b(?:take me (?:on|through|to)|give me|start|begin|launch|run|show me|do|open|play|restart|resume)(?:\s+(?:me|us|the|a|an|quick|short|guided|product|symbols|workspace|full|little))*\s+tours?(?:\s+of\s+(?:symbols|the (?:workspace|app|platform|product)))?(?:\s+(?:please|now|again))?\s*[.!]*$/.test(
      t
    ) ||
    /^(?:the |a )?(?:symbols |product |quick |guided |workspace )*tour(?: please)?\s*[.!]*$/.test(
      t
    ) ||
    /\bshow (?:me|us) around\b/.test(t) ||
    /\bhow (?:does|do) (?:symbols|this|it|the (?:workspace|app|platform)) work\b.*\bshow me\b/.test(
      t
    ) ||
    /\bshow me how (?:symbols|this|it|the (?:workspace|app|platform)) works?\b/.test(t) ||
    /(?:^|\s)ტურ(?:ი|ს|ზე|ით)?\s*[.!]*$/.test(t) ||
    /^(?:ჩამიტარე|დამიწყე|დაიწყე|მაჩვენე|გამატარე|გამიკეთე)(?:თ)?\s+ტურ/.test(t) ||
    /(?:^|\s)მაჩვენე(?:თ)?\s+როგორ\s+მუშაობს/.test(t) ||
    new RegExp('(?:^|\\s)დამათვალიერებინე(?:თ)?' + end).test(t) ||
    /(?:^|\s)გამაცანი(?:თ)?(?:\s+(?:symbols|სიმბოლს)\S*)?\s*[.!]*$/.test(t)
  if (tour) return 'tour'

  // A NEGATION near the start is never a request.
  if (/^(?:\S+\s+){0,3}(?:do not|don't|dont|never|no need to)\b/.test(t)) return 'question'
  if (/^(?:\S+\s+){0,2}(?:არ|ნუ)\s/.test(t)) return 'question'

  // THE LEAD-IN: courtesy and request frames before the verb.
  const leadIn =
    /^(?:hey|hi|hello|ok|okay|so|now|then|and|also|alright|great|cool|thanks|thank you|please|pls|kindly|quickly|just|go ahead and|could you|can you|would you mind|would you|will you|i want you to|i'd like you to|i would like you to|i need you to|i want to|i'd like to|i would like to|i need to|i want|i need|i'd like|let's|lets|let us|help me|try to|maybe|გთხოვთ|გთხოვ|თუ შეიძლება|შეგიძლიათ|შეგიძლია|ახლა|კარგი|მინდა რომ|მინდა|მსურს|მჭირდება|დამეხმარეთ|დამეხმარე|მოდი)(?:[\s,.!:;-]+|$)/
  let s = t
  for (let i = 0; i < 6; i++) {
    const next = s.replace(leadIn, '')
    if (next === s) break
    s = next
  }
  if (!s) return 'question'

  // THE OBJECT NOUNS, by the surface they name. The Georgian stems follow
  // docs/I18N-GLOSSARY.md: a draft is დრაფტი, and the Blueprint word is Fly's
  // workspace plan, never a document.
  const objects = [
    [
      'website',
      /\b(?:web ?sites?|sites?|landing(?: ?pages?)?|home ?pages?|web ?pages?|portfolio(?: site)?|blog(?! posts?))\b|(?:^|\s)(?:ვებსაიტ|საიტ|ვებგვერდ|ლენდინგ|სადესანტო|ბლოგ)/g
    ],
    [
      'deck',
      /\b(?:presentations?|slide ?decks?|decks?|slides?|slideshows?|pitch(?: decks?)?)\b|(?:^|\s)(?:პრეზენტაცი|სლაიდ|დეკ(?=$|[\s.,!?ისზ]))/g
    ],
    [
      'app',
      /\b(?:apps?|applications?|extensions?|plug-?ins?|mini-?apps?|trackers?|portals?|crm|tools?|boards?)\b|(?:^|\s)(?:აპლიკაცი|აპ(?:ი|ს|ის)?(?=$|[\s.,!?])|ექსთენშენ|პლაგინ|ტრეკერ|პორტალ|ბორდ|დაფ)/g
    ],
    [
      'dashboard',
      /\b(?:dashboards?|charts?|graphs?|reports?|analytics|kpis?|metrics|visuali[sz]ations?|tables?|pages?|funnels?|leaderboards?|scorecards?|heat ?maps?)\b|(?:^|\s)(?:დაშბორდ|დეშბორდ|გრაფიკ|დიაგრამ|ანგარიშ|რეპორტ|ანალიტიკ|ცხრილ|გვერდ)/g
    ],
    [
      'document',
      /\b(?:documents?|docs?|notes?|memos?|plans?|proposals?|strateg(?:y|ies)|briefs?|outlines?|drafts?|articles?|essays?|letters?|polic(?:y|ies)|specs?|specifications?|checklists?|agendas?|minutes|write-?ups?|posts?|playbooks?|guides?|wiki|readme|faqs?|scripts?|contracts?)\b|(?:^|\s)(?:დოკუმენტ|დოკ(?=$|[\s.,!?ისზ])|შენიშვნ|ჩანაწერ|გეგმ|სტრატეგი|წინადადებ|მემო|სტატი|წერილ|ოქმ|დრაფტ|ხელშეკრულებ)/g
    ],
    [
      'action',
      /\b(?:tickets?|tasks?|to-?dos?|events?|meetings?|calls?|invites?|invitations?|contacts?|leads?|deals?|invoices?|customers?|clients?|reminders?|channels?|folders?|projects?|records?|entries|entry|rows?|columns?|fields?|e-?mails?|messages?|mails?|replies|reply|comments?|issues?|bugs?|appointments?|bookings?|orders?|users?|members?|teammates?)\b|(?:^|\s)(?:ტიკეტ|დავალებ|შეხვედრ|ღონისძიებ|კონტაქტ|ლიდ|გარიგებ|ინვოის|ზარ|მეილ|ელფოსტ|შეტყობინებ|არხ|საქაღალდ|პროექტ|კლიენტ|შეკვეთ|წევრ)/g
    ]
  ]
  // Every object word in a phrase, each as its whole word; a word inside a
  // longer one is dropped ("landing page" holds "page").
  const objectsIn = (str) => {
    const found = []
    for (const [cls, re] of objects) {
      re.lastIndex = 0
      let m = re.exec(str)
      while (m) {
        const at = m.index + (m[0].length - m[0].replace(/^\s+/, '').length)
        let stop = m.index + m[0].length
        while (stop < str.length && !/[\s.,!?;:()]/.test(str[stop])) stop++
        found.push({ cls, at, end: stop, word: str.slice(at, stop) })
        if (!m[0].length) re.lastIndex++
        m = re.exec(str)
      }
    }
    return found.filter(
      (a) =>
        !found.some((b) => b !== a && b.at <= a.at && b.end >= a.end && b.end - b.at > a.end - a.at)
    )
  }
  // THE HEAD NOUN of the object. English: the LAST object word of the first
  // noun phrase (modifiers lead — "a client feedback board" is a board, "a
  // lead generation strategy document" a document); the phrase ends at a
  // preposition, a conjunction or a mark. Georgian: the first object word in
  // the nominative (the direct object of a request — "დოკუმენტი ლიდების
  // სტრატეგიით" is a document), else the first one.
  const stops =
    /^(?:of|for|with|about|on|in|into|to|that|which|who|whose|showing|shows|show|from|by|per|and|or|but|using|based|where|so|as|at|called|named|titled|comparing|including|listing|containing|tracking|covering|between|across|over|under|than|like|then|while|when|if|because|since|after|before|until|via|against|around|without|within|is|are|was|were|has|have|look|looks)$/
  const headOf = (tail) => {
    const str = String(tail || '').trim()
    if (!str) return null
    const words = str.split(/\s+/)
    if (/[ა-ჿ]/.test(str)) {
      const room = words.slice(0, 8).join(' ')
      const all = objectsIn(room).sort((a, b) => a.at - b.at)
      if (!all.length) return null
      const head =
        all.find((m) => /[იაე]$/.test(m.word) && !/(?:ის|ით|ზე|ში|თვის|დან|ად)$/.test(m.word)) ||
        all[0]
      return {
        ...head,
        first: head.at === 0,
        size: words.length,
        rest: room.slice(head.end).trim()
      }
    }
    const phrase = []
    for (const w of words) {
      const bare = w.replace(/[,.;:!?()]+$/, '')
      if (phrase.length && stops.test(bare)) break
      phrase.push(bare)
      if (bare !== w || phrase.length >= 8) break
    }
    const np = phrase.join(' ')
    const all = objectsIn(np)
    if (!all.length) return null
    const head = all.reduce((a, b) => (b.at > a.at ? b : a))
    return {
      ...head,
      first: head.at === 0,
      size: phrase.length,
      rest: words.slice(phrase.length).join(' ')
    }
  }
  const isBoard = (h) => !!h && h.cls === 'app' && /^(?:boards?|ბორდ|დაფ)/.test(h.word)
  // A BOARD that describes data ("a board of my tickets", "a board showing
  // …") is a dashboard; any other board you build is an app ("a client
  // feedback board"), as the builder has always taken it. A Georgian ბორდი is
  // the workspace's dashboard word; a დაფა (the glossary's Board) is data when
  // the words after it say what it shows ("დაფა ჩემი დავალებებით", "დაფა
  // გაყიდვებზე", "დაფა, რომელიც აჩვენებს …").
  const boardKind = (h) =>
    /^ბორდ/.test(h.word) ||
    /^(?:of|showing|with|that shows|comparing|by|per)\b/.test(h.rest) ||
    (/^დაფ/.test(h.word) &&
      (/^[,\s]*(?:რომელიც|სადაც)/.test(h.rest) ||
        h.rest
          .split(/\s+/)
          .slice(0, 4)
          .some((w) => /(?:ით|ზე)[.,!?]*$/.test(w))))
      ? 'dashboard'
      : 'app'
  const verb = (re) => {
    const m = re.exec(s)
    return m ? s.slice(m[0].length).trim() : null
  }

  // THE VERB at the start — each family in both languages.
  if (verb(/^(?:visuali[sz]e|chart|plot|graph)\b/) !== null) return 'dashboard'
  const comm = verb(
    new RegExp(
      '^(?:send|e-?mail|mail|message|text|dm|reply(?: to)?|respond(?: to)?|forward|schedule|reschedule|book|invite|remind|assign|reassign|cancel|approve|reject|decline|accept|pay|refund|submit|upload|install|uninstall|connect|subscribe|unsubscribe|post|notify|ping|write (?:back )?to|გა(?:უ|მი)?გზავნ(?:ე|ეთ|ო|ოთ|ა)|და(?:მი)?ნიშნ(?:ე|ეთ|ო|ოთ)|შემახსენ(?:ე|ეთ)|მი(?:ს|უ)?წერ(?:ე|ეთ)|მოიწვი(?:ე|ეთ)|მიანიჭ(?:ე|ეთ)|გაუზიარ(?:ე|ეთ))' +
        end
    )
  )
  if (comm !== null) return 'action'
  const open = verb(
    new RegExp(
      '^(?:open(?: up)?|re-?open|go to|go back to|take me to|bring me to|navigate to|jump to|switch to|pull up|bring up|launch|გა(?:მი|ვ)?ხს(?:ენი|ენით|ნა|ნათ|ნი|ნით)|გადადი(?:თ)?|გადამიყვანე(?:თ)?|წამიყვანე(?:თ)?)' +
        end
    )
  )
  // "open a new document" asks for a new one: the object decides, below.
  if (open !== null && !/^(?:(?:a|an)\s+)?new\b|^ახალი\s/.test(open)) return 'open'
  const write = verb(
    new RegExp(
      '^(?:write(?: up| down)?|draft|compose|jot(?: down)?|type up|draw up|და(?:მი|ვ)?წერ(?:ე|ეთ|ო|ოთ|ა|ათ))' +
        end
    )
  )
  if (write !== null) {
    const h = headOf(write)
    if (h && ['website', 'deck'].includes(h.cls)) return h.cls
    if (h && h.cls === 'app' && !isBoard(h)) return 'app'
    if (h && h.cls === 'action' && /mail|message|repl|მეილ|ელფოსტ|შეტყობინებ/.test(h.word)) {
      return 'action'
    }
    return 'document'
  }
  const create =
    open !== null
      ? open
      : verb(
          new RegExp(
            '^(?:build|make|create|generate|prepare|put together|design|set up|setup|spin up|produce|mock up|whip up|craft|assemble|develop|code|შე(?:მი|ვ)?ქმ(?:ენი|ენით|ნა|ნათ|ნი|ნით)|გა(?:ა|მი|ვა|უ)?კეთ(?:ე|ეთ|ო|ოთ|ება)|ა(?:ა|მი|ვა)?წყ(?:ვე|ვეთ|ე|ეთ|ო|ოთ|ობა)|ა(?:ა|მი|ვა)?გ(?:ე|ეთ|ო|ოთ|ება)|მო(?:ა|მი|ვა)?მზად(?:ე|ეთ|ო|ოთ|ება)|და(?:ა|მი|ვა)?გენერირ(?:ე|ეთ|ო|ოთ|ება)|და(?:მი|ვ)?ხატ(?:ე|ეთ|ო|ოთ|ვა))' +
              end
          )
        )
  if (create !== null) {
    // "make sure …", "make it shorter", "make this better" edit what is there.
    if (
      /^(?:sure|certain|it|this|that|them|these|those|everything|anything|something)\b/.test(create)
    ) {
      return 'question'
    }
    const h = headOf(create.replace(/^(?:me|us)\s+/, ''))
    if (!h) return 'question'
    return isBoard(h) ? boardKind(h) : h.cls
  }
  const show = verb(
    new RegExp('^(?:show(?: me| us)?|display|give me|get me|(?:მა|მი|ა)ჩვენ(?:ე|ეთ))' + end)
  )
  if (show !== null) {
    const h = headOf(show)
    if (!h) return 'question'
    // "show me the sales dashboard" names a thing that exists: open it.
    const named =
      /^(?:the|my|our|this|that|these|those|your|ჩემი|ჩვენი|თქვენი|ეს|ამ)\s/.test(show) &&
      h.size <= 5
    if (named && h.cls !== 'action') return 'open'
    if (h.cls === 'dashboard' || isBoard(h)) return 'dashboard'
    if (h.cls === 'deck') return 'deck'
    return 'question'
  }
  const edit = verb(
    new RegExp(
      '^(?:add|update|edit|change|rename|delete|remove|archive|unarchive|restore|mark|close|move|set|tag|label|pin|unpin|log|record|enable|disable|turn on|turn off|merge|duplicate|copy|convert|transfer|complete|finish|export|import|share|attach|link|unlink|და(?:ა|მი|ვა)?მატ(?:ე|ეთ|ო|ოთ|ება)|წა(?:მი)?შალ(?:ე|ეთ)|შე(?:მი)?ცვალ(?:ე|ეთ)|განაახლ(?:ე|ეთ)|გადა(?:ი|მი)ტან(?:ე|ეთ))' +
        end
    )
  )
  if (edit !== null) {
    const h = headOf(edit)
    if (h) return h.cls === 'action' ? 'action' : 'question'
    // "add Nika to the project": no object in the phrase — any record word
    // further on makes it an action.
    return objectsIn(edit.split(/\s+/).slice(0, 8).join(' ')).some((m) => m.cls === 'action')
      ? 'action'
      : 'question'
  }

  // A QUESTION by its shape.
  if (
    /^(?:what|what's|whats|which|who|whom|whose|when|where|why|how|is|are|am|was|were|do|does|did|can|could|should|would|will|shall|may|might|have|has|had|any|tell me|explain|describe|summari[sz]e|list|find|look up|search)\b/.test(
      s
    ) ||
    /\?\s*$/.test(s) ||
    /^(?:რა|რას|რის|რით|როგორ|რატომ|როდის|სად|საიდან|ვინ|ვის|რომელ|რამდენ|ხომ|არის|გაქვს|გაქვთ|შეიძლება|მითხარი|მითხარით|ამიხსენი|ამიხსენით)/.test(
      s
    )
  ) {
    return 'question'
  }

  // A NOUN PHRASE that IS the ask: "a board of my tickets", "new presentation",
  // "dashboard of sales by month", "ახალი პრეზენტაცია", "დაშბორდი გაყიდვებზე".
  const article = /^(?:a|an|new|another|one|ახალი)\s+/.exec(s)
  const h = headOf(article ? s.slice(article[0].length) : s)
  if (!h || h.cls === 'action') return 'question'
  const georgian = /[ა-ჿ]/.test(h.word)
  const whole = georgian
    ? s.split(/\s+/).length <= 6 && (article || h.first)
    : h.size <= 4 &&
      (article || h.first) &&
      (!h.rest ||
        /^(?:of|for|about|on|with|showing|that shows|comparing|by|per|from|to)\b/.test(h.rest))
  if (!whole) return 'question'
  return isBoard(h) ? boardKind(h) : h.cls
}

// THE VIEW the next ask lands in — the ONE source the three labels read (the
// thread head's micro-label, the composer's placeholder, the Ask menu's
// check): 'chat' | 'pages' | 'build' | 'present' | 'home'.
//   - the main thread holds the main area → 'chat' ('build' in Apps mode, the
//     builder's conversation — the home tabs read it so too);
//   - a page sheet open in the PageSlot → 'chat' (the composer speaks for the
//     page; an ask there is a chat turn beside it);
//   - the Fly canvas → 'pages'; a live deck → 'present';
//   - the home with its tabs → the tab (homeView.js homeViewOf: Main 'pages',
//     Dashboard 'home', Present, Build);
//   - otherwise the mode: Fly and a stored Dashboard read as 'pages', Assistant
//     and Tools as 'chat', Apps as 'build', Presentation as 'present'.
// Every key is read up front (v3.14 tracking: a reactive caller subscribes to
// all of them whichever branch returns).
export const assistantEffectiveView = function assistantEffectiveView(root) {
  const r = root && typeof root === 'object' ? root : {}
  const fns = (this && this.context && this.context.functions) || {}
  const mode = r.assistantMode
  const pane = !!(r._canvasSlot || r._canvasSlotSecond)
  void [r.assistantDock, r._homeMode, r.currentPath, r._aiFly, r._homeThreadOpen]
  const thread =
    typeof fns.assistantMainThreadOpen === 'function'
      ? !!this.call('assistantMainThreadOpen', r)
      : false
  const fly =
    typeof fns.assistantFlyShown === 'function' ? !!this.call('assistantFlyShown', r) : false
  const deck =
    typeof fns.assistantDeckShown === 'function' ? !!this.call('assistantDeckShown', r) : false
  const tabs =
    typeof fns.homeTabsOnScreen === 'function' && typeof fns.homeViewOf === 'function'
      ? !!this.call('homeTabsOnScreen', r)
      : false
  const tab = tabs ? this.call('homeViewOf', r) : null
  if (thread) return mode === 'apps' ? 'build' : 'chat'
  if (pane) return 'chat'
  if (tabs) {
    const byTab = { main: 'pages', chat: 'chat', present: 'present', build: 'build' }
    return byTab[tab] || 'home'
  }
  if (fly) return 'pages'
  if (deck) return 'present'
  if (mode === 'fly' || mode === 'dashboard') return 'pages'
  if (mode === 'presentation') return 'present'
  if (mode === 'apps') return 'build'
  return 'chat'
}

// THE DECISION for one ask: { intent, view, wants, surface, route, mode,
// switched, back, unavailable, probe }.
//   surface  'tour' | 'app' | 'site' | 'fly' | 'board' | 'deck' | 'thread' |
//            'drawer' (today's docked drawer / reasoning pill path)
//   route    what homeAiSend dispatches on: the surface, with 'drawer' → null
//   mode     the ask's own mode for a chat turn (`sendPrompt` opts.mode):
//            'assistant' when the turn leaves a non-conversation view, null to
//            ride the live mode (Assistant, Tools; Apps for its questions)
//   switched the ask left the view's own surface — one quiet line says so
//   back     the way back that line offers: 'chat' | 'pages' | 'home' | null
//   unavailable  'pages' | 'slides' — wanted, but the board service has not
//            answered yes here: the chat answers, and says so once
//   probe    ask the board service first, then decide again (`opts.probed`)
// `opts`: { intent? (a caller that already knows), probed? }.
export const assistantAskRoute = function assistantAskRoute(root, text, opts) {
  const r = root && typeof root === 'object' ? root : {}
  const o = opts && typeof opts === 'object' ? opts : {}
  const fns = (this && this.context && this.context.functions) || {}
  const has = (name) => typeof fns[name] === 'function'
  const mode = r.assistantMode
  const main = r.assistantDock === 'main'
  const pane = !!(r._canvasSlot || r._canvasSlotSecond)
  const boards = r._aiBoardsAvailable === true
  const intent =
    typeof o.intent === 'string' && o.intent ? o.intent : this.call('assistantAskIntent', text)
  const view = has('assistantEffectiveView') ? this.call('assistantEffectiveView', r) : 'chat'
  const chat = !pane && main ? 'thread' : 'drawer'
  // A stored Dashboard mode keeps its board meaning: "build a client feedback
  // board" there is a page; from any other view the builder makes it
  // (homeAiSendBuild round2c). An app, a tracker or a portal is always an app.
  const ask = String(text == null ? '' : text).toLowerCase()
  const boardPage =
    intent === 'app' &&
    mode === 'dashboard' &&
    /\bboards?\b|(?:^|\s)(?:ბორდ|დაფ)/.test(ask) &&
    !/\b(?:apps?|applications?|trackers?|portals?|crm|extensions?|plug-?ins?|tools?)\b|აპლიკაცი|ტრეკერ|პორტალ/.test(
      ask
    )
  const wants =
    intent === 'tour'
      ? 'tour'
      : intent === 'app' && !boardPage
        ? 'app'
        : intent === 'website'
          ? 'site'
          : pane
            ? 'chat'
            : intent === 'dashboard' || boardPage
              ? 'pages'
              : intent === 'deck'
                ? 'deck'
                : intent === 'question'
                  ? view === 'pages'
                    ? 'pages'
                    : view === 'present'
                      ? 'deck'
                      : 'chat'
                  : 'chat'
  let surface = chat
  let unavailable = null
  // The tour's gate is the Fly start tile's own (assistantFlyTourOffered:
  // aiStartTour and onboardingTourStart registered) — one start, one gate.
  const tourOn = has('assistantFlyTourOffered')
    ? !!this.call('assistantFlyTourOffered')
    : has('aiStartTour') && has('onboardingTourStart')
  if (wants === 'tour' && tourOn) surface = 'tour'
  else if (wants === 'app' && has('forceBuildApp')) surface = 'app'
  else if (wants === 'site' && has('assistantOpenSiteBuilder')) surface = 'site'
  else if (wants === 'pages') {
    // Fly takes the main area on any placement; a generated board needs the
    // main placement. With the service up but no room for a board, the chat
    // answers without a note (nothing is unavailable).
    if (boards && has('assistantFlyGenerate')) surface = 'fly'
    else if (boards && main && has('assistantBoardGenerate')) surface = 'board'
    else if (!boards) unavailable = 'pages'
  } else if (wants === 'deck') {
    if (boards && main && has('assistantDeckGenerate')) surface = 'deck'
    else if (!boards) unavailable = 'slides'
  }
  const probe = !!unavailable && !o.probed && !boards && has('assistantBoardProbe')
  const family = (x) =>
    x === 'fly' || x === 'board' ? 'pages' : x === 'thread' || x === 'drawer' ? 'chat' : x
  // The view's own surfaces: Build's conversation is the builder's, so an app
  // and its questions both stay home there.
  const own =
    view === 'pages'
      ? ['pages']
      : view === 'present'
        ? ['deck']
        : view === 'build'
          ? ['app', 'chat']
          : ['chat']
  const switched = !pane && !unavailable && surface !== 'tour' && !own.includes(family(surface))
  const back = !switched
    ? null
    : view === 'pages'
      ? 'pages'
      : view === 'home'
        ? 'home'
        : view === 'present'
          ? null
          : 'chat'
  const conversation = surface === 'thread' || surface === 'drawer'
  const keepsMode =
    mode === 'assistant' || mode === 'tools' || (mode === 'apps' && intent === 'question')
  return {
    intent,
    view,
    wants,
    surface,
    route: surface === 'drawer' ? null : surface,
    mode: conversation && !keepsMode ? 'assistant' : null,
    switched,
    back,
    unavailable,
    probe
  }
}

// SAY WHERE IT WENT. A switch: one quiet toast line with one way back (the
// shell's toast host — shared methods/notification.js; never modal). Pages or
// slides the board service cannot make here: one note IN the thread, once
// per conversation (a `routeNote` row the next ask finds). Returns true when
// it said something.
export const assistantAskNotify = function assistantAskNotify(decision) {
  const d = decision && typeof decision === 'object' ? decision : null
  const root = this && this.state && this.state.root
  if (!d || !root || typeof root.update !== 'function') return false
  if (d.unavailable) {
    const list = Array.isArray(root.chatMessages) ? root.chatMessages : []
    const said = list.some(
      (m) => m && m.metadata && m.metadata.kind === 'routeNote' && m.metadata.note === d.unavailable
    )
    if (said) return false
    const key =
      d.unavailable === 'slides'
        ? 'workspace.askRoute.slidesUnavailable'
        : 'workspace.askRoute.pagesUnavailable'
    const now = Date.now()
    root.update(
      {
        chatMessages: list.concat({
          id: 'sys-route-' + now.toString(36) + Math.random().toString(36).slice(2, 6),
          role: 'system',
          text: String(this.call('polyglot', key)),
          ts: now,
          metadata: { kind: 'routeNote', note: d.unavailable }
        })
      },
      { preventFetch: true }
    )
    return true
  }
  if (!d.switched) return false
  const lines = {
    fly: 'workspace.askRoute.toPages',
    board: 'workspace.askRoute.toPages',
    deck: 'workspace.askRoute.toSlides',
    app: 'workspace.askRoute.toApp',
    site: 'workspace.askRoute.toSite',
    thread: 'workspace.askRoute.toChat',
    drawer: 'workspace.askRoute.toChat'
  }
  const backs = {
    chat: 'workspace.askRoute.backToChat',
    pages: 'workspace.askRoute.backToPages',
    home: 'workspace.askRoute.backToHome'
  }
  const line = lines[d.surface]
  // The toast host is a METHOD (shared methods/notification.js): look it up
  // the way `call` does (utils → functions → methods → snippets).
  const c = this.context || {}
  const toast = ['utils', 'functions', 'methods', 'snippets'].some(
    (k) => c[k] && typeof c[k].openNotification === 'function'
  )
  if (!line || !toast) return false
  const back = backs[d.back] ? d.back : null
  try {
    this.call('openNotification', {
      title: String(this.call('polyglot', line)),
      ...(back
        ? { actionLabel: backs[back], action: { call: 'assistantAskBack', args: [back] } }
        : {}),
      duration: 6000
    })
  } catch (_) {
    return false
  }
  return true
}

// THE WAY BACK a switch offers. On the home with its tabs it is the tab
// (homeViewSelect — the home's local mode, nothing persists); elsewhere the
// view itself: the chat thread (or the docked drawer), the Fly canvas, or the
// page under the views.
export const assistantAskBack = function assistantAskBack(target) {
  const root = this && this.state && this.state.root
  if (!root || typeof root.update !== 'function') return false
  const fns = (this.context && this.context.functions) || {}
  const has = (name) => typeof fns[name] === 'function'
  const main = root.assistantDock === 'main'
  const tabs =
    main &&
    has('homeViewSelect') &&
    has('homeTabsOnScreen') &&
    !!this.call('homeTabsOnScreen', root)
  if (target === 'pages') {
    if (tabs) {
      this.call('homeViewSelect', 'main')
      return true
    }
    if (has('assistantMainThreadClose')) this.call('assistantMainThreadClose')
    return has('assistantFlyWorkspaceEnter') ? !!this.call('assistantFlyWorkspaceEnter') : false
  }
  if (target === 'home') {
    if (tabs) {
      this.call('homeViewSelect', 'dashboard')
      return true
    }
    for (const name of [
      'assistantFlyClose',
      'assistantBoardDismiss',
      'assistantDeckDismiss',
      'assistantMainThreadClose'
    ]) {
      if (has(name)) this.call(name)
    }
    return true
  }
  if (target !== 'chat') return false
  if (tabs) {
    this.call('homeViewSelect', 'chat')
    return true
  }
  for (const name of ['assistantFlyClose', 'assistantBoardDismiss', 'assistantDeckDismiss']) {
    if (has(name)) this.call(name)
  }
  if (main && has('assistantMainThreadShow')) return !!this.call('assistantMainThreadShow')
  root.update({ assistantOpen: true })
  return true
}

// THE SHARED ENTRY POINTS — a thread chip, the sidebar Chat's send, a starter
// (shared/_simone ChatMessage, Chat, AssistantStarter). They keep their own
// conversation, unless the ask clearly wants another surface (a page, an app,
// slides, a website, the tour): then it goes the composer's way (homeAiSend,
// which decides, falls back and says so). Returns true when it took the ask.
// The builder's own chat never comes here (the callers pass 'general' asks
// only), nor an ask while Build mode is armed (sendPrompt turns it into a
// build).
export const assistantAskHandoff = function assistantAskHandoff(text, opts) {
  const root = this && this.state && this.state.root
  const fns = (this && this.context && this.context.functions) || {}
  const ask = String(text == null ? '' : text).trim()
  if (!root || !ask || root._aiBuildMode) return false
  if (typeof fns.homeAiSend !== 'function' || typeof fns.assistantAskIntent !== 'function') {
    return false
  }
  const intent = this.call('assistantAskIntent', ask)
  if (!['tour', 'dashboard', 'app', 'deck', 'website'].includes(intent)) return false
  const from = opts && typeof opts.from === 'string' ? opts.from : 'chat'
  this.call('homeAiSend', ask, null, 0, null, { from })
  return true
}

// SPLIT VIEW: a page sheet that opens over the main thread (a tool opened it
// in split, or the person opened a link from an answer) would cover the
// conversation, and the next ask would close it. On a wide screen the
// conversation moves BESIDE the sheet instead — the session promotion to the
// docked sidebar ("Open in sidebar", assistantThreadToSidebar), so closing the
// drawer returns to the main placement and "Open in main" brings it back. On a
// phone (≤ mobileL, `_isPhoneViewport`) the sheet is full-screen and its own
// close is the way back to the thread under it. Run by the thread view on
// every pane change (components/AssistantMainThread.js). Every key is read up
// front.
export const assistantThreadBesidePane = function assistantThreadBesidePane(root) {
  const r = root && typeof root === 'object' ? root : null
  if (!r || typeof r.update !== 'function') return false
  const pane = !!(r._canvasSlot || r._canvasSlotSecond)
  const phone = r._isPhoneViewport === true
  const open = !!this.call('assistantMainThreadOpen', r)
  if (!pane || phone || !open) return false
  return !!this.call('assistantThreadToSidebar')
}
